import { AnchorButton, ButtonAsLink } from '@cluey/cluey-components';
import classNames from 'classnames';
import { ChevronDown, ChevronUp } from 'lucide-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useId, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import isEmail from 'validator/lib/isEmail';

import { saveSelectedSession } from '../../../actions/hub/hubContactPageActions';
import clockIcon from '../../../assets/images/icon-clock--orange.svg';
import tickIcon from '../../../assets/images/icon-filled-alert-tick.svg';
import raysIcon from '../../../assets/images/icon-filled-rays.svg';
import SessionTag from '../../../common/SessionTag/SessionTag';
import { useGetSessionPolicy } from '../../../hooks/queries/useSessionQuery';
import { getPkgTutorNameByEnrolmentId } from '../../../selectors/hubEnrolmentsSelector';
import { SessionType } from '../../../types/hubPropTypes';
import {
  DATE_FORMAT_TYPE_13,
  STUDENT_PROGRESS_TAB_VIEWS,
  STUDENT_PROGRESS_URL_PARAM_TAB,
  TTBM_CUSTOMER_NUMBER,
} from '../../../util/constants';
import { formatSession } from '../../../util/helpers';
import { PATH_HUB_CR_REQUEST_RECORDING, PATH_NAME_REPORT } from '../../../util/pagePath';
import VimeoPlayer, { isVimeoVideoUrl } from '../../../v2/common/VimeoPlayer';
import { DesktopOnly } from '../../DesktopOnly/DesktopOnly';
import { SessionDetails } from './HubSessionsListItem/SessionDetails';
import SessionPaymentInfo from './SessionPaymentInfo';

const CustomLink = ({ link, label, className }) => (
  <a
    href={link}
    rel="noopener noreferrer"
    target="_blank"
    className={classNames('lh-1 mb-2 block text-primary hover:underline', className)}
  >
    {label}
  </a>
);

CustomLink.propTypes = {
  link: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const HubCompletedSessionsListItem = ({ session, timezone, saveSelectedSessionToState }) => {
  const {
    student,
    subject,
    uniqueEnrolmentId,
    sessionReportId,
    tutor,
    recordingLink,
    tutorNumber,
    startTime,
    endTime,
    classroomPractice,
    type,
  } = session;
  const getFormattedTime = (time) => moment(time).tz(timezone).format('h:mma');
  const timeFrame = `${getFormattedTime(startTime)} - ${getFormattedTime(endTime)}`;
  const packageTutor = useSelector((state) => getPkgTutorNameByEnrolmentId(state, uniqueEnrolmentId));
  const displayTutor = tutorNumber === TTBM_CUSTOMER_NUMBER ? packageTutor : tutor;
  const isClassroomSession = type === 'Classroom';
  const isClassroomPracticeDone =
    classroomPractice && classroomPractice.questionsCompleted === classroomPractice.questionsTotal;
  const { data: sessionPolicy } = useGetSessionPolicy({ sessionId: session?.studentSessionId });
  const hasPlayableRecording = isVimeoVideoUrl(recordingLink);
  const [isRecordingExpanded, setIsRecordingExpanded] = useState(false);
  const recordingPanelId = useId();

  const history = useHistory();
  const onClickHandler = () => {
    saveSelectedSessionToState({
      session,
    });

    history.push(PATH_HUB_CR_REQUEST_RECORDING);
  };

  const { date } = formatSession({
    session,
    timezone,
    sessionDateFormat: DATE_FORMAT_TYPE_13,
    useSemiColon: false,
  });

  const recordingOption = () => {
    if (hasPlayableRecording) {
      return (
        <button
          type="button"
          aria-expanded={isRecordingExpanded}
          aria-controls={recordingPanelId}
          onClick={() => setIsRecordingExpanded((expanded) => !expanded)}
          className="lh-1 mb-2 flex w-full items-center gap-1 p-0 text-link hover:text-orange-400 lg:justify-end"
        >
          <span className="link">View session recording</span>
          {isRecordingExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      );
    }

    if (recordingLink && isEmail(recordingLink)) {
      return (
        <span className="lh-1 mb-2 block text-grey-400 lg:text-right">
          Preparing session recording
        </span>
      );
    }

    return (
      <ButtonAsLink
        size="base"
        className="ls-normal lh-1-14 border-radius-0 link block w-full p-0 text-left font-body hover:underline md:text-right"
        onClick={onClickHandler}
      >
        Request session recording
      </ButtonAsLink>
    );
  };

  const courseOptions = (className?: string) => (
    <div className={className}>
      <CustomLink
        link={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.SESSION_REPORT}`}
        label="View learning activities"
        className="link text-left lg:text-right"
      />
      {recordingOption()}
    </div>
  );

  const classroomOptions = (className?: string) => (
    <div className={classNames('flex flex-col items-start gap-2 lg:items-end', className)}>
      <AnchorButton
        size="sm"
        className="mb-1 hidden min-w-[80px] !px-6 normal-case lg:inline-flex lg:items-center lg:justify-center"
        href={classroomPractice.practiceSessionLink}
      >
        {isClassroomPracticeDone ? 'View practice questions' : 'Practice what I learnt'}
      </AnchorButton>
      {!isClassroomPracticeDone && (
        <div className="flex flex-row items-center gap-x-2">
          <img
            src={classroomPractice.questionsCompleted === 0 ? raysIcon : clockIcon}
            className="h-5 w-5"
            alt="questions icon"
          />
          <span>
            {classroomPractice.questionsCompleted} of {classroomPractice.questionsTotal} questions completed
          </span>
        </div>
      )}
      {isClassroomPracticeDone && (
        <div className="flex flex-row items-center gap-x-2">
          <img src={tickIcon} className="h-5 w-5" alt="tick icon" />
          <span>All questions completed</span>
        </div>
      )}
    </div>
  );

  return (
    <div
      data-testid="session-list-item"
      className="relative flex w-full flex-col items-start gap-1 rounded-lg border-2 border-grey-2 px-5 py-3 shadow-md xl:flex"
    >
      <div className="flex w-full flex-col lg:flex-row">
        <div className="flex w-full grow basis-2/3 flex-col items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <SessionDetails student={student} subject={subject} tutor={displayTutor} />
            <span className="font-[Proxima-Soft] text-xs font-bold uppercase">{date}</span>
            <div className="text-base font-normal">{timeFrame}</div>
          </div>
          {!isClassroomSession && <SessionPaymentInfo session={session} />}
          <SessionTag session={session} policy={sessionPolicy} className="my-1.5" />
          <DesktopOnly className={isClassroomSession ? 'flex' : 'hidden'} />
          {isClassroomSession && classroomOptions('flex lg:hidden')}
          {!isClassroomSession && courseOptions('flex flex-col lg:hidden')}
        </div>
        <div className="hidden w-full basis-1/3 flex-col items-end gap-1 lg:flex">
          {isClassroomSession && classroomOptions('justify-items-end')}
          {!isClassroomSession && courseOptions()}
        </div>
      </div>
      {hasPlayableRecording && isRecordingExpanded && (
        <div id={recordingPanelId} className="w-full pb-1 pt-2">
          <VimeoPlayer videoUrl={recordingLink} />
        </div>
      )}
    </div>
  );
};

HubCompletedSessionsListItem.propTypes = {
  session: SessionType.isRequired,
  timezone: PropTypes.string.isRequired,
  saveSelectedSessionToState: PropTypes.func.isRequired,
};

export default connect(null, (dispatch) => {
  return {
    saveSelectedSessionToState: bindActionCreators(saveSelectedSession, dispatch),
  };
})(HubCompletedSessionsListItem);

export { HubCompletedSessionsListItem as StatelessHubCompletedSessionsListItem };
